import React, { createContext, useEffect, useReducer } from "react";
import { CreateAccountFormValues } from "../forms/account/create-account-form";
import { EditAccountFormValues } from "../forms/account/edit-account-form";
import { LoginFormValues } from "../forms/auth/login-form";
import { Account } from "../models/account";
import { AccountService } from "../services/account-service";
import { CognitoService } from "../services/cognito-service";

type State = {
  loading: boolean;
  usernameToConfirm: string;
  isLoggedIn: boolean;
  isAdmin: boolean;
  account: Account | null;
};

type Action =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_CONFIRMATION_USERNAME"; payload: string }
  | {
      type: "SET_ACCOUNT";
      payload: { account: Account | null; isAdmin?: boolean };
    };
type Reducer = (state: State, action: Action) => State;

type Actions = {
  signUp: (values: CreateAccountFormValues) => Promise<void>;
  signIn: (values: LoginFormValues) => Promise<void>;
  signOut: () => Promise<void>;
  notConfirmedAccount: (username: string) => void;
  confirmAccount: (code: string, username: string) => Promise<void>;
  editAccount: (data: EditAccountFormValues) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  confirmPassword: (
    code: string,
    email: string,
    password: string
  ) => Promise<void>;
};

const AuthContext = createContext<undefined | (State & Actions)>(undefined);

const reducer: Reducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_CONFIRMATION_USERNAME":
      return { ...state, usernameToConfirm: action.payload };
    case "SET_ACCOUNT":
      return {
        ...state,
        account: action.payload.account,
        isLoggedIn: !!action.payload.account,
        isAdmin: action.payload?.account ? !!action.payload?.isAdmin : false,
      };
  }
};

const defaultState: State = {
  loading: true,
  usernameToConfirm: "",
  isLoggedIn: false,
  isAdmin: false,
  account: null,
};

const initializer = (defaultState: State): State => {
  const state = localStorage.getItem("auth-state");
  return state ? JSON.parse(state) : defaultState;
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, defaultState, initializer);

  const loader = async (cb: () => Promise<any>) => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      await cb();
    } catch (e) {
      throw e;
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const actions: Actions = {
    signUp: async (values) =>
      await loader(async () => {
        await CognitoService.signUp(values);
        dispatch({
          type: "SET_CONFIRMATION_USERNAME",
          payload: values.email,
        });
      }),
    signIn: async (values) =>
      await loader(async () => {
        const session = await CognitoService.authenticateUser(values);
        const isAdmin = CognitoService.isAdmin(session);
        await new Promise((resolve) => setTimeout(resolve, 1200));
        const account = await AccountService.getAccount();
        dispatch({ type: "SET_ACCOUNT", payload: { account, isAdmin } });
      }),
    signOut: async () =>
      await loader(async () => {
        await CognitoService.signOut();
        dispatch({ type: "SET_ACCOUNT", payload: { account: null } });
      }),
    notConfirmedAccount: (username) =>
      dispatch({ type: "SET_CONFIRMATION_USERNAME", payload: username }),
    confirmAccount: async (code, username) =>
      await loader(async () => {
        await CognitoService.confirmRegistration(code, username);
        dispatch({ type: "SET_CONFIRMATION_USERNAME", payload: "" });
      }),
    editAccount: async (data) =>
      await loader(async () => {
        if (state.account?.email !== data.email)
          await CognitoService.changeEmail(data.email);

        const editedAccount = await AccountService.editAccount({
          id: state.account!.id,
          firstName: data.firstName,
          lastName: data.lastName,
          county: data.county,
          municipality: data.municipality,
          phone: data.phone,
          email: data.email,
          address1: data.streetAddress,
          address2: data.aptSuiteBuilding,
          city: data.city,
          stateId: data.state,
          postalCode: data.zipCode,
        });

        dispatch({ type: "SET_ACCOUNT", payload: { account: editedAccount } });
      }),
    forgotPassword: async (username) =>
      await loader(async () => await CognitoService.forgotPassword(username)),
    confirmPassword: async (code, email, password) =>
      await loader(
        async () =>
          await CognitoService.confirmPassword(code, email, password)
      ),
  };

  useEffect(() => {
    (async () =>
      await loader(async () => {
        const session = await CognitoService.getSession();
        if (!session) return;

        const account = await AccountService.getAccount();
        const isAdmin = CognitoService.isAdmin(session);

        dispatch({ type: "SET_ACCOUNT", payload: { account, isAdmin } });
      }))();
  }, []);

  useEffect(
    () => localStorage.setItem("auth-state", JSON.stringify(state)),
    [state]
  );

  return (
    <AuthContext.Provider value={{ ...state, ...actions }}>
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined)
    throw new Error("useAuth must be used within a AuthProvider");

  return context;
}

export { useAuth, AuthProvider };
